import { useState, useEffect } from "react";
import { Alert, Button, Form, Modal, ListGroup, Spinner } from "react-bootstrap";
import { deleteProduct, updateProduct, getProductPlaces } from "../api";

export default function EditProduct({
    show,
    onHide,
    jwt,
    onUpdate,
    onDeleted,
    categories = [],
    product = {},
    places = []
}) {
    const [productLocal, setProductLocal] = useState({
        id: product?.id || 0,
        name: product?.name || '',
        coefficient: product?.coefficient || 1,
        categoryId: product?.categoryId || null,
        placeIds: []
    });

    const [onDelete, setOnDelete] = useState(null);
    const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Загрузка привязанных маркетплейсов при открытии
    useEffect(() => {
        if (show && product?.id) {
            loadProductPlaces();
        } else {
            setProductLocal({
                id: product?.id || 0,
                name: product?.name || '',
                coefficient: product?.coefficient || 1,
                categoryId: product?.categoryId || null,
                placeIds: []
            });
        }
    }, [show, product?.id]);

    const loadProductPlaces = async () => {
        setIsLoadingPlaces(true);
        try {
            const productPlaces = await getProductPlaces(product.id, jwt);
            setProductLocal(prev => ({
                ...prev,
                placeIds: productPlaces?.map(p => p.productPlaceId) || []

            }));
            console.log(productPlaces?.map(p => p.productPlaceId));
        } catch (error) {
            console.error("Ошибка загрузки маркетплейсов:", error);
            alert("Не удалось загрузить привязанные маркетплейсы");
        } finally {
            setIsLoadingPlaces(false);
        }
    };

    const togglePlaceSelection = (placeId) => {
        setProductLocal(prev => ({
            ...prev,
            placeIds: prev.placeIds.includes(placeId)
                ? prev.placeIds.filter(id => id !== placeId)
                : [...prev.placeIds, placeId]
        }));
    };

    const handleSubmit = async () => {
        try {
            const success = await updateProduct(productLocal.id, {
                name: productLocal.name,
                coefficient: productLocal.coefficient,
                categoryId: productLocal.categoryId,
                placeIds: productLocal.placeIds
            }, jwt);

            if (success) {
                const updatedProduct = {
                    ...product,
                    ...productLocal,
                    places: productLocal.placeIds.map(id => ({
                        productPlaceId: id,
                        name: places.find(p => p.id === id)?.name || ''
                    }))
                };
                onUpdate(updatedProduct);
                onHide();
            }
        } catch (error) {
            console.error("Ошибка при обновлении:", error);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteProduct(product.id, jwt);
            onDeleted();
            setOnDelete(false);
        } catch (error) {
            console.error("Ошибка при удалении:", error);
            alert("Произошла ошибка при удалении товара");
        }
    };

    // Модальное окно подтверждения удаления
    if (onDelete) {
        return (
            <Modal centered show={onDelete} onHide={() => setOnDelete(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Подтверждение удаления</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        Вы уверены, что хотите удалить этот товар? Все связанные данные будут также удалены.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setOnDelete(false)}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Удалить
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal centered show={show} onHide={onHide} size="lg">
            <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактирование товара</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Название товара</Form.Label>
                        <Form.Control
                            required
                            type="text"
                            value={productLocal.name}
                            onChange={(e) => setProductLocal({ ...productLocal, name: e.target.value })}
                            placeholder="Введите название товара"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Коэффициент</Form.Label>
                        <Form.Control
                            required
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={productLocal.coefficient}
                            onChange={(e) => setProductLocal({
                                ...productLocal,
                                coefficient: parseFloat(e.target.value) || 0
                            })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Категория</Form.Label>
                        <Form.Select
                            value={productLocal.categoryId || ''}
                            onChange={(e) => setProductLocal({
                                ...productLocal,
                                categoryId: e.target.value ? parseInt(e.target.value) : null
                            })}
                        >
                            <option value="">Без категории</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            Доступные маркетплейсы
                            {isLoadingPlaces && (
                                <Spinner animation="border" size="sm" className="ms-2" />
                            )}
                        </Form.Label>
                        <ListGroup>
                            {places.map(place => (
                                <ListGroup.Item
                                    key={place.id}
                                    action
                                    active={productLocal.placeIds.includes(place.id)}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        togglePlaceSelection(place.id)
                                    }}
                                    disabled={isLoadingPlaces}
                                >
                                    {place.name}
                                    {productLocal.placeIds.includes(place.id) && (
                                        <span className="float-end">✓</span>
                                    )}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        onClick={() => setOnDelete(true)}
                        disabled={isSubmitting}
                    >
                        Удалить
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        disabled={isSubmitting}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || isLoadingPlaces}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Сохранение...
                            </>
                        ) : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}